import { CanvasDisplayPrecision, CanvasRenderingCtx } from '../rendering';
import { CircleAnnotation, FreeAnnotation, RulerAnnotation, SquareAnnotation, TextAnnotation, UserAnnotation } from './annotation';
import { PhotoEditorAnnotationCanvasRenderer } from './photo-editor.decorator';
import { FreePenRenderer } from './free-pen.renderer';
import { CirclePenRenderer } from './circle-pen.renderer';
import { SquarePenRenderer } from './square-pen.renderer';
import { TextPenRenderer } from './text-pen.renderer';
import { RulerPenRenderer } from './ruler-pen.renderer';

const FLATTEN_RENDERING_CTX: CanvasRenderingCtx = { sx: 1, sy: -1, tx: 0, ty: 0, rx: 0, precision: CanvasDisplayPrecision.FULL };

const DATA_URL_MIME = /^data:(image\/[a-zA-Z+]+);base64,/;

export async function flattenAnnotationsOntoImage(imageSrc: string, annotations: UserAnnotation[] | undefined, quality = 0.92): Promise<string> {
  if (!annotations?.length) {
    return imageSrc;
  }

  const image = await loadImage(imageSrc);

  if (typeof document !== 'undefined') {
    try {
      await document.fonts.ready;
    } catch {}
  }

  const width = image.naturalWidth || image.width;
  const height = image.naturalHeight || image.height;

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext('2d', { alpha: false });
  if (!ctx) {
    throw new Error('Could not acquire 2D context for annotation flattening');
  }

  ctx.translate(0, height);
  ctx.scale(1, -1);

  ctx.save();
  ctx.translate(0, height);
  ctx.scale(1, -1);
  ctx.drawImage(image, 0, 0);
  ctx.restore();

  for (const annotation of annotations) {
    createRenderer(ctx, annotation)?.draw(FLATTEN_RENDERING_CTX);
  }

  const outputMime = DATA_URL_MIME.exec(imageSrc)?.[1] ?? 'image/jpeg';
  return canvas.toDataURL(outputMime, quality);
}

function createRenderer(ctx: CanvasRenderingContext2D, annotation: UserAnnotation): PhotoEditorAnnotationCanvasRenderer<UserAnnotation> | null {
  switch (annotation.type) {
    case 'free':
      return new FreePenRenderer(ctx, annotation as FreeAnnotation);
    case 'circle':
      return new CirclePenRenderer(ctx, annotation as CircleAnnotation);
    case 'square':
      return new SquarePenRenderer(ctx, annotation as SquareAnnotation);
    case 'text':
      return new TextPenRenderer(ctx, annotation as TextAnnotation);
    case 'ruler':
      return new RulerPenRenderer(ctx, annotation as RulerAnnotation);
    default:
      console.warn(`Unknown annotation type, skipping during flatten: ${annotation.type}`);
      return null;
  }
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = document.createElement('img');
    img.onload = () => {
      img.onload = null;
      img.onerror = null;
      resolve(img);
    };
    img.onerror = () => {
      img.onload = null;
      img.onerror = null;
      reject(new Error('Failed to load image for annotation flattening'));
    };
    img.src = src;
  });
}
